import { If, onMount, reactive } from 'cdui-js';
import { DiFiNavBar } from '../../../components/DiFi/DiFiNavBar';
import i18n from '../../../i18n';
import { goBackByBrowser } from '../../../lib/browser';
import {
  agreementDocViewState,
  isRichTextContentType,
  looksLikeHtmlBody,
  resolveAgreementFileUrl,
  resolveAgreementViewKind,
  type AgreementViewKind,
} from '../../../lib/agreement-doc-view';
import { stripAgreementHtmlThemeColors } from '../../../lib/agreement-html-theme';
import { isLegalEmbeddedInApp } from '../../legal/legalEmbed';
import type { AgreementSceneDocItem } from '../../../api/agreement-doc';

async function resolveBody(
  doc: AgreementSceneDocItem,
): Promise<{ kind: Exclude<AgreementViewKind, 'pdf' | 'rich_text'>; body: string }> {
  let body = '';

  // 1) 显式 content 字段
  if (doc.content != null && String(doc.content) !== '') {
    body = String(doc.content);
  } else if (
    doc.fileUrl &&
    (isRichTextContentType(doc.contentType) || looksLikeHtmlBody(doc.fileUrl))
  ) {
    // 2) RICH_TEXT / 内联 HTML：fileUrl 即正文（详情页兜底；正常应走弹层）
    body = String(doc.fileUrl);
  } else if (doc.fileUrl) {
    // 3) TEXT / HTML 文件地址：CDN 拼接后拉取
    const url = resolveAgreementFileUrl(doc.fileUrl);
    if (!url) {
      return { kind: 'text', body: '' };
    }
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`fetch agreement content failed: ${res.status}`);
    }
    body = await res.text();
  }

  const kind = resolveAgreementViewKind(doc, body);
  if (kind === 'pdf') {
    return { kind: 'text', body };
  }
  // rich_text 在详情页按 html 渲染；HTML 剥硬编码色以跟随主题
  if (kind === 'rich_text' || kind === 'html') {
    return { kind: 'html', body: stripAgreementHtmlThemeColors(body) };
  }
  return { kind, body };
}

export const AgreementDocPage = () => {
  const embedded = isLegalEmbeddedInApp();
  const state = reactive({
    loading: true,
    title: '',
    kind: 'text' as Exclude<AgreementViewKind, 'pdf' | 'rich_text'>,
    body: '',
    error: '',
  });

  onMount(async () => {
    const doc = agreementDocViewState.doc;
    if (!doc) {
      state.loading = false;
      state.error = i18n.Common.AgreementNotFound;
      return;
    }

    state.title = doc.name || doc.docCode;
    console.log('agreement-doc page', {
      docCode: doc.docCode,
      contentType: doc.contentType,
      fileUrl: doc.fileUrl,
      hasContent: !!(doc.content && String(doc.content)),
    });

    try {
      const resolved = await resolveBody(doc);
      state.kind = resolved.kind;
      state.body = resolved.body;
      if (!resolved.body) state.error = i18n.Common.AgreementContentEmpty;
    } catch (e) {
      console.error('agreement-doc resolveBody failed', e);
      state.error = i18n.Common.AgreementLoadFailed;
    } finally {
      state.loading = false;
    }
  });

  const pageClass = embedded
    ? 'agreement-doc-page page-wrapper-no-header flex-column bg-c-D'
    : 'agreement-doc-page page-wrapper-with-header flex-column bg-c-D';

  return (
    <div class={pageClass}>
      <If when={!embedded}>
        <DiFiNavBar title={state.title || i18n.Common.Agreement} fixed class="bg-c" onClickLeft={() => goBackByBrowser()} />
      </If>

      <div
        class="flex-1 no-scrollbar padding-x padding-b-L"
        style={{ 'overflow-y': 'auto', 'padding-top': embedded ? '16px' : '54px' }}
      >
        <If when={state.loading}>
          <div class="font-s-S color margin-t">加载中...</div>
        </If>

        <If when={!state.loading && state.error}>
          <div class="font-s-S color-error margin-t">{state.error}</div>
        </If>

        {/* HTML / 富文本 */}
        <If when={!state.loading && !state.error && state.kind === 'html'}>
          <div
            class="agreement-doc-html font-s-S color-D"
            style={{ 'line-height': '22px' }}
            innerHTML={state.body}
          />
        </If>

        {/* 纯文本 */}
        <If when={!state.loading && !state.error && state.kind === 'text'}>
          <div
            class="agreement-doc-text font-s-S color-D"
            style={{ 'line-height': '22px', 'white-space': 'pre-wrap' }}
          >
            {state.body}
          </div>
        </If>
      </div>
    </div>
  );
};

export default AgreementDocPage;
